import { useState, useEffect, useCallback, useRef } from "react"
import { AuditService } from "@/services/audit-service"
import type { AuditHistoryDocument, AuditResponse } from "@/types/audit"

interface UseAuditHistoryReturn {
  records: AuditHistoryDocument[]
  loading: boolean
  error: string | null
  totalCount: number
  currentPage: number
  hasMore: boolean
  searchTerm: string
  loadHistory: (itemId: string, reset?: boolean) => Promise<void>
  loadMore: () => Promise<void>
  setSearchTerm: (term: string) => void
  clearError: () => void
}

export function useAuditHistory(
  itemId?: string,
  pageSize: number = 20
): UseAuditHistoryReturn {
  const [records, setRecords] = useState<AuditHistoryDocument[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  
  // Use ref to track if we've already loaded data for this itemId
  const loadedItemIdRef = useRef<string | null>(null)
  const searchTermRef = useRef<string>("")

  const loadHistory = useCallback(
    async (targetItemId: string, reset: boolean = false, searchValue?: string) => {
      if (!targetItemId) return

      try {
        setLoading(true)
        setError(null)

        const page = reset ? 1 : currentPage
        const currentSearchTerm = searchValue ?? searchTermRef.current
        
        const response = await AuditService.getAuditHistoryForItem(
          targetItemId,
          page,
          pageSize,
          currentSearchTerm
        )

        // Handle the actual API response structure (array of records)
        if (Array.isArray(response)) {
          const records = response as AuditHistoryDocument[]
          if (reset) {
            setRecords(records)
            setCurrentPage(1)
          } else {
            setRecords(prev => [...prev, ...records])
          }
          
          // Since the API returns all records at once, we'll set hasMore based on pageSize
          setTotalCount(records.length)
          setHasMore(records.length >= pageSize)
        } else {
          // Fallback to expected AuditResponse structure if it changes
          const auditResponse = response as AuditResponse
          if (reset) {
            setRecords(auditResponse.records)
            setCurrentPage(1)
          } else {
            setRecords(prev => [...prev, ...auditResponse.records])
          }
          setTotalCount(auditResponse.totalCount)
          setHasMore(auditResponse.hasMore)
        }
      } catch (err) {
        // Don't set error state - just log it and show empty results
        console.warn('Audit history API error:', err)
        if (reset) {
          setRecords([])
          setTotalCount(0)
          setHasMore(false)
        }
      } finally {
        setLoading(false)
      }
    },
    [currentPage, pageSize] // Removed searchTerm dependency
  )

  const loadMore = useCallback(async () => {
    if (!itemId || !hasMore || loading) return

    setCurrentPage(prev => prev + 1)
    await loadHistory(itemId, false)
  }, [itemId, hasMore, loading, loadHistory])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Load initial history when itemId changes (only once)
  useEffect(() => {
    if (itemId && itemId !== loadedItemIdRef.current) {
      loadedItemIdRef.current = itemId
      searchTermRef.current = ""
      loadHistory(itemId, true)
    }
  }, [itemId, loadHistory])

  // Handle search term changes separately
  useEffect(() => {
    if (itemId && searchTerm !== searchTermRef.current) {
      searchTermRef.current = searchTerm
      // Reset and reload with new search term
      loadHistory(itemId, true, searchTerm)
    }
  }, [searchTerm, itemId, loadHistory])

  return {
    records,
    loading,
    error,
    totalCount,
    currentPage,
    hasMore,
    searchTerm,
    loadHistory,
    loadMore,
    setSearchTerm,
    clearError,
  }
}
